import { DeleteOutlined, LockOutlined, PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Drawer, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import apiClient from "@/app/api/client";
import { usePagination } from "@/shared/hooks/usePagination";
import { userService } from "../services/user.service";

const { Title } = Typography;

const roleLabels: Record<number, { label: string; color: string }> = {
	100: { label: "Admin", color: "purple" },
	200: { label: "Viloyat", color: "blue" },
	300: { label: "Tuman", color: "green" },
	400: { label: "To'yxona", color: "orange" },
};

export function UsersPage() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();
	const { page, pageSize, onChange } = usePagination();
	const [search, setSearch] = useState("");
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [pwdModal, setPwdModal] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
	const [form] = Form.useForm();
	const [pwdForm] = Form.useForm();

	const { data, isLoading } = useQuery({
		queryKey: ["users", { page, per_page: pageSize, search }],
		queryFn: () => userService.list({ page, per_page: pageSize, search: search || undefined }),
	});

	const saveMutation = useMutation({
		mutationFn: (v: Record<string, unknown>) => editingId ? userService.update(editingId, v) : userService.create(v),
		onSuccess: () => {
			message.success(editingId ? "Yangilandi" : "Yaratildi");
			closeDrawer();
			queryClient.invalidateQueries({ queryKey: ["users"] });
		},
		onError: () => message.error("Xatolik"),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => userService.remove(id),
		onSuccess: () => { message.success("O'chirildi"); queryClient.invalidateQueries({ queryKey: ["users"] }); },
	});

	const statusMutation = useMutation({
		mutationFn: ({ id, status }: { id: number; status: string }) => userService.updateStatus(id, status),
		onSuccess: () => { message.success("Holat o'zgartirildi"); queryClient.invalidateQueries({ queryKey: ["users"] }); },
	});

	const resetPwdMutation = useMutation({
		mutationFn: ({ id, password }: { id: number; password: string }) => userService.resetPassword(id, password),
		onSuccess: () => { message.success("Parol yangilandi"); setPwdModal({ open: false, id: null }); pwdForm.resetFields(); },
	});

	const openCreate = () => { setEditingId(null); form.resetFields(); setDrawerOpen(true); };
	const openEdit = (record: Record<string, unknown>) => { setEditingId(record.id as number); form.setFieldsValue(record); setDrawerOpen(true); };
	const closeDrawer = () => { setDrawerOpen(false); setEditingId(null); form.resetFields(); };

	const users = data?.data?.users || [];
	const pagination = data?.data?.pagination;

	const columns = [
		{ title: "ID", dataIndex: "id", width: 60 },
		{ title: "Ism", dataIndex: "name" },
		{ title: "Login", dataIndex: "username" },
		{ title: "Telefon", dataIndex: "phone" },
		{
			title: "Rol", key: "role",
			render: (_: unknown, r: { role?: { id: number; name: string } }) => {
				const rl = r.role ? roleLabels[r.role.id] : null;
				return <Tag color={rl?.color || "default"}>{rl?.label || r.role?.name || "—"}</Tag>;
			},
		},
		{
			title: "Holat", dataIndex: "status",
			render: (v: string, r: { id: number }) => (
				<Tag color={v === "active" ? "green" : "red"} className="cursor-pointer"
					onClick={() => statusMutation.mutate({ id: r.id, status: v === "active" ? "disabled" : "active" })}>
					{v}
				</Tag>
			),
		},
		{
			title: "Amallar", width: 140,
			render: (_: unknown, r: Record<string, unknown>) => (
				<Space>
					<Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
					<Button size="small" icon={<LockOutlined />} onClick={() => setPwdModal({ open: true, id: r.id as number })} title="Parol tiklash" />
					<Popconfirm title="O'chirish?" onConfirm={() => deleteMutation.mutate(r.id as number)}>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Title level={3} className="!mb-0">Foydalanuvchilar</Title>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Qo'shish</Button>
			</div>
			<Input placeholder="Qidiruv..." prefix={<SearchOutlined />} className="mb-4 max-w-xs" value={search} onChange={(e) => setSearch(e.target.value)} allowClear />
			<Table columns={columns} dataSource={users} rowKey="id" loading={isLoading}
				pagination={{ current: pagination?.current_page, pageSize: pagination?.per_page, total: pagination?.total_items, onChange, showSizeChanger: true }} />

			<Drawer title={editingId ? "Foydalanuvchi tahrirlash" : "Yangi foydalanuvchi"} open={drawerOpen} onClose={closeDrawer} width={480}
				extra={<Space><Button onClick={closeDrawer}>Bekor qilish</Button><Button type="primary" onClick={() => form.submit()} loading={saveMutation.isPending}>Saqlash</Button></Space>}>
				<UserFormContent form={form} editingId={editingId} onFinish={(v) => saveMutation.mutate(v)} />
			</Drawer>

			<Modal title="Parol tiklash" open={pwdModal.open} onCancel={() => setPwdModal({ open: false, id: null })}
				onOk={() => pwdForm.submit()} confirmLoading={resetPwdMutation.isPending}>
				<Form form={pwdForm} layout="vertical" onFinish={(v) => pwdModal.id && resetPwdMutation.mutate({ id: pwdModal.id, password: v.new_password })}>
					<Form.Item name="new_password" label="Yangi parol" rules={[{ required: true, min: 6 }]}><Input.Password /></Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

// ==================== User Form with SOATO ====================

function UserFormContent({ form, editingId, onFinish }: { form: ReturnType<typeof Form.useForm>[0]; editingId: number | null; onFinish: (v: Record<string, unknown>) => void }) {
	const roleId = Form.useWatch("role_id", form);
	const regionId = Form.useWatch("region_id", form);

	const needsRegion = roleId === 200 || roleId === 300;
	const needsDistrict = roleId === 300;

	const { data: regionsData } = useQuery({
		queryKey: ["regions"],
		queryFn: () => apiClient.get("/regions", { params: { per_page: 100 } }).then((r) => r.data),
		enabled: needsRegion,
	});

	const { data: districtsData } = useQuery({
		queryKey: ["districts", regionId],
		queryFn: () => apiClient.get("/districts", { params: { region_id: regionId, per_page: 100 } }).then((r) => r.data),
		enabled: needsDistrict && !!regionId,
	});

	return (
		<Form form={form} layout="vertical" onFinish={onFinish}>
			<Form.Item name="name" label="To'liq ism" rules={[{ required: true }]}><Input /></Form.Item>
			<Form.Item name="username" label="Login" rules={[{ required: true }]}><Input disabled={!!editingId} /></Form.Item>
			<Form.Item name="phone" label="Telefon"><Input /></Form.Item>
			{!editingId && (
				<Form.Item name="password" label="Parol" rules={[{ required: true, min: 6 }]}><Input.Password /></Form.Item>
			)}
			<Form.Item name="role_id" label="Rol" rules={[{ required: true }]}>
				<Select
					options={Object.entries(roleLabels).map(([k, v]) => ({ value: Number(k), label: v.label }))}
					onChange={() => { form.setFieldValue("region_id", undefined); form.setFieldValue("district_id", undefined); }}
				/>
			</Form.Item>
			{needsRegion && (
				<Form.Item name="region_id" label="Viloyat" rules={[{ required: true, message: "Viloyat tanlang" }]}>
					<Select placeholder="Tanlang" allowClear showSearch optionFilterProp="label"
						options={regionsData?.data?.regions?.map((r: { id: number; title: string }) => ({ value: r.id, label: r.title }))}
						onChange={() => form.setFieldValue("district_id", undefined)} />
				</Form.Item>
			)}
			{needsDistrict && (
				<Form.Item name="district_id" label="Tuman" rules={[{ required: true, message: "Tuman tanlang" }]}>
					<Select placeholder="Avval viloyat tanlang" allowClear disabled={!regionId} showSearch optionFilterProp="label"
						options={districtsData?.data?.districts?.map((d: { id: number; title: string }) => ({ value: d.id, label: d.title }))} />
				</Form.Item>
			)}
		</Form>
	);
}
